import * as React from 'react';
import { ClickBustingConfig } from '../config/ClickBusting';
import { conditional, makeTestId } from '../utils/Declarative';
import { InputLabelWidget } from './InputLabelWidget';

const SwitcherTextGroupNamePrefix = 'SwitcherTextGroup';

export interface SwitcherTextBaseStyle {
  activeSelectedToggle: React.CSSProperties;
  activeUnselectedToggle: React.CSSProperties;
  container: React.CSSProperties;
  disabledUnselectedToggle: React.CSSProperties;
  disabledSelectedToggle: React.CSSProperties;
  firstToggle: React.CSSProperties;
  focusSelectedToggle: React.CSSProperties;
  focusUnselectedToggle: React.CSSProperties;
  hoverSelectedToggle: React.CSSProperties;
  hoverUnselectedToggle: React.CSSProperties;
  inactiveToggle: React.CSSProperties;
  lastToggle: React.CSSProperties;
  selectedToggle: React.CSSProperties;
  toggleBase: React.CSSProperties;
  unselectedToggle: React.CSSProperties;
}

export interface SwitcherTextBaseProps {
  ariaLabel?: string;
  checkedIndex: number;
  className?: string;
  disabled?: boolean;
  handleChange: (idx: number) => void;
  /** The input name attribute */
  name?: string;
  style: SwitcherTextBaseStyle;
  testId: string;
  toggleTexts: string[];
}

export interface SwitcherTextBaseState {
  activerSwitcherIdx: number;
}

interface SwitcherButtonProps {
  checked?: boolean;
  disabled?: boolean;
  groupName: string;
  handleActive: (idx: number) => void;
  handleChange: (idx: number) => void;
  disabledStyle: React.CSSProperties;
  focusStyle: React.CSSProperties;
  hoverStyle: React.CSSProperties;
  idx: number;
  style: React.CSSProperties;
  testId: string;
}

/**
 * The SwitchTextButton is the individual button within a switcher.  It contains the text and
 * can be toggled.  The underlying input is a radio button.
 */
class SwitcherTextButton extends React.Component<SwitcherButtonProps, {}> {
  private handleChange = (e: React.FormEvent<HTMLInputElement>): void => {
    if (!this.props.checked) {
      this.props.handleChange(this.props.idx);
    }
  }

  /**
   * If it is active, call the handleActive with the switcher's idx, otherwise return -1 to say
   * that it is no longer selected.
   */
  private handleActive = (active: boolean): void => {
    this.props.handleActive(active ? this.props.idx : -1);
  }

  public render(): React.ReactElement {
    return (
      // tslint:disable:deprecation
      <InputLabelWidget
        disabled={this.props.disabled}
        handleActive={this.handleActive}
        interactiveStyles={{
          disabled: this.props.disabledStyle,
          focusVisible: this.props.focusStyle,
          hover: this.props.hoverStyle,
        }}
        style={this.props.style}
        testId={this.props.testId}
      >
        <input
          checked={this.props.checked}
          data-tb-test-id={makeTestId(this.props.testId, 'input')}
          disabled={this.props.disabled}
          name={this.props.groupName}
          onChange={this.handleChange}
          style={{
            opacity: 0,
            position: 'absolute'
          }}
          type='radio'
        />
        {this.props.children}
      </InputLabelWidget>
      // tslint:enable:deprecation
    );
  }
}

/**
 * The SwitchTextBase is the container of the switcher buttons.  It maintains the state of what
 * switcher button is active and creates the group id for the underlying radio buttons.
 */
export class SwitcherTextBase extends React.Component<SwitcherTextBaseProps, SwitcherTextBaseState> {
  private static groupIdIncrementor: number = 0;
  private groupId: number;

  public constructor(props: SwitcherTextBaseProps) {
    super(props);
    this.groupId = SwitcherTextBase.groupIdIncrementor++;
    this.state = { activerSwitcherIdx: -1 };
  }

  private handleActive = (idx: number): void => {
    // TFSID 694578: prevent visual flicker on iOS when client is ClickBusting
    if (ClickBustingConfig.suppressionClassName && ClickBustingConfig.suppressionClassName.length) { return; }

    this.setState({ activerSwitcherIdx: idx });
  }

  private getToggleStyling(currentSwitcherIdx: number): React.CSSProperties {
    const hasActiveSwitcher = this.state.activerSwitcherIdx >= 0;
    const currentSwitcherIsFirst = currentSwitcherIdx === 0;
    const currentSwitcherIsLast = currentSwitcherIdx === this.props.toggleTexts.length - 1;
    const currentSwitcherInactive = this.state.activerSwitcherIdx !== currentSwitcherIdx;
    const currentSwitcherChecked = this.props.checkedIndex === currentSwitcherIdx;

    return {
      ...this.props.style.toggleBase,
      ...conditional(currentSwitcherIsFirst, this.props.style.firstToggle),
      ...conditional(currentSwitcherIsLast, this.props.style.lastToggle),
      ...(hasActiveSwitcher ? (currentSwitcherInactive ? this.props.style.inactiveToggle :
               /* is active */ currentSwitcherChecked ? this.props.style.activeSelectedToggle : this.props.style.activeUnselectedToggle)
            /* no active */ : (currentSwitcherChecked ? this.props.style.selectedToggle : this.props.style.unselectedToggle)
      )
    };
  }

  public render(): React.ReactElement {
    const groupName = SwitcherTextGroupNamePrefix + String(this.groupId);

    const switchButtons = this.props.toggleTexts.map((toggleText: string, idx: number) => {
      const isChecked: boolean = this.props.checkedIndex === idx;
      const toggleStyle = this.getToggleStyling(idx);
      const hoverStyle = isChecked ? this.props.style.hoverSelectedToggle : this.props.style.hoverUnselectedToggle;
      const focusStyle = isChecked ? this.props.style.focusSelectedToggle : this.props.style.focusUnselectedToggle;
      const disabledStyle = isChecked ? this.props.style.disabledSelectedToggle : this.props.style.disabledUnselectedToggle;

      return <SwitcherTextButton
        checked={isChecked}
        children={toggleText}
        disabled={this.props.disabled}
        disabledStyle={disabledStyle}
        focusStyle={focusStyle}
        groupName={this.props.name || groupName}
        handleActive={this.handleActive}
        handleChange={this.props.handleChange}
        hoverStyle={hoverStyle}
        idx={idx}
        key={idx}
        style={toggleStyle}
        testId={makeTestId(this.props.testId, toggleText)}
      />;
    });

    return <div
      aria-label={this.props.ariaLabel}
      children={switchButtons}
      className={[ClickBustingConfig.suppressionClassName, this.props.className].join(' ')}
      role='radiogroup'
      style={this.props.style.container}
    />;
  }
}
